import { ParameterName, notNullish } from "@hex/common";
import { useCallback } from "react";

import { useCellsContentsGetter } from "../../hex-version-multiplayer/state-hooks/cellsContentsStateHooks";

import { generateNameForDuplicate } from "./useGetUniqueVariableName";

/**
 * get a unique name for a new input cell, first trying the provided name
 * if specified. When a name is provided, disambiguates like SQL/other cells
 * (e.g. "my_input" -> "my_input_2", "my_input_2" -> "my_input_3").
 * When no name is provided, uses default "input_1", "input_2", etc.
 */
export function useGetUniqueInputCellName(): (
  /**
   * pass if you want the new cell's name to be based off of some previous name
   */
  name?: string,
  /**
   * pass if you're generating names for multiple cells in a loop (to avoid the same name being generated each time)
   */
  extraExistingNames?: ParameterName[],
) => ParameterName {
  const getCellsContents = useCellsContentsGetter();

  return useCallback(
    (name, extraExistingNames) => {
      const inputCells = Object.values(getCellsContents())
        .filter(notNullish)
        .flatMap((c) => (c.__typename === "Parameter" ? c : []));

      const existingInputNames = [
        ...inputCells.map((inputCell) => inputCell.name),
        ...(extraExistingNames ?? []),
      ];

      if (name != null && name !== "") {
        return generateNameForDuplicate(
          name,
          existingInputNames,
        ) as ParameterName;
      }

      let count = 0;
      let testName: string;
      const existingSet = new Set(existingInputNames);
      do {
        count++;
        testName = `input_${count}`;
      } while (existingSet.has(testName as ParameterName));

      return testName as ParameterName;
    },
    [getCellsContents],
  );
}
